// src/components/PlanVsActualConstruction.js
import React, { useEffect } from "react";
import Highcharts, { solidGauge } from "highcharts";
import HighchartsReact from "highcharts-react-official";
// import solidGauge from "highcharts/modules/solid-gauge";
import more from "highcharts/highcharts-more";
import { Avatar, Box, Divider, Skeleton, styled, Typography, useTheme, Grid2, ListItemIcon, ListItemText, List, ListItem } from "@mui/material";
import { useGetConstructionPlanVsActualQuery } from "store/slices/apis/constructionDashboardSlice ";
import { useParams } from "react-router-dom";
import { FaSquareFull } from "react-icons/fa6";

// Initialize modules
if (typeof more === "function") {
    more(Highcharts);
}

const PlanVsActualConstruction = () => {
    const theme = useTheme();
    const { projectId } = useParams();
    const { data, isLoading } = useGetConstructionPlanVsActualQuery({ projectId });
    const actual = data?.actualPercentage ?? 0;
    const planned = data?.plannedPercentage ?? 0;
    const overall = data?.overallTarget ?? 100;
    let options = {
        chart: {
            type: "solidgauge",
            height: 309,
        },
        title: {
            text: "",
            style: { fontSize: "18px" },
        },
        pane: {
            startAngle: 0,
            endAngle: 360,
            size: '100%',
            background: [
                {
                    outerRadius: "",
                    innerRadius: "88%",
                    backgroundColor: Highcharts.color("#7cb5ec").setOpacity(0.3).get(),
                    borderWidth: 0,
                },
                {
                    outerRadius: "",
                    innerRadius: "63%",
                    backgroundColor: Highcharts.color("#90ed7d").setOpacity(0.3).get(),
                    borderWidth: 0,
                },
                {
                    outerRadius: "",
                    innerRadius: "38%",
                    backgroundColor: Highcharts.color("#f7a35c").setOpacity(0.3).get(),
                    borderWidth: 0,
                },
            ],
        },
        yAxis: {
            min: 0,
            max: 100,
            lineWidth: 0,
            tickPositions: [],
        },
        plotOptions: {
            solidgauge: {
                dataLabels: {
                    enabled: false,
                },
                linecap: "round",
                stickyTracking: false,
            },
        },
        legend: {
            enabled: true,
            align: "center",
            verticalAlign: "bottom",
            layout: "horizontal",
        },
        series: [
            {
                name: "Overall",
                data: [{ y: overall, color: "#E8E3E3", radius: "90%", innerRadius: "100%" }],
            },
            {
                name: "Plan",
                data: [{ y: planned, color: "#295477", radius: "75%", innerRadius: "85%" }],
            },
            {
                name: "Actual",
                data: [{ y: actual, color: actual >= planned ? '#3E756D' : '#E72B33', radius: "60%", innerRadius: "70%" }],
            

            },
        ],
        credits: {
            enabled: false,
        },
    };


    return (
        <Box
            sx={{
                p: 2,
                pb: 0,
                pr: 0,
                border: '1px solid #2525252E',
                borderRadius: '7px',
                height: '100%',
                background: "#FFFFFF",
                width: "100%",
            }}
        >
            <Grid2 container>
                <Grid2 size={{ xs: 12, md: 12 }}>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: "500",
                            fontSize: '18px',
                            color: `${theme?.customSettings?.themeColor}`,
                            lineHeight: '100%',
                            letterSpacing: '-0.4px',
                        }}
                    >
                        Physical Progress - Plan vs Actual
                    </Typography>
                    <Typography
                        variant="caption"
                        sx={{
                            fontWeight: "500",
                            fontSize: '14px',
                            color: `#182A4E80`,
                            lineHeight: '100%',
                            letterSpacing: '-0.4px',
                        }}
                    >
                        Progress variance and  execution shortfalls against the project baseline
                    </Typography>
                </Grid2>

                <Grid2 size={{ xs: 12, md: 6 }}>
                    {isLoading ? (
                        <List sx={{ padding: 0, mt: 4 }}>
                            {[1, 2, 3].map((_, i) => (
                                <ListItem key={i} sx={{ paddingLeft: 0 }}>
                                    <Skeleton variant="circular" width={16} height={16} sx={{ mr: 1 }} />
                                    <Skeleton variant="text" width="80%" height={20} />
                                </ListItem>
                            ))}
                        </List>
                    ) : (
                        <List sx={{ padding: 0, mt: 4 }}>
                            {options?.series?.map((val, index) => (
                                <ListItem key={index} sx={{ paddingLeft: 0 }}>
                                    <ListItemIcon sx={{ minWidth: '20px' }}>
                                        <FaSquareFull
                                            style={{
                                                color: `${val?.data[0]?.color}`,
                                                height: '16px',
                                                width: '16px',
                                            }}
                                        />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={`${val?.name}: ${val?.data[0]?.y}%`}
                                        sx={{
                                            color: "#182A4E",
                                            whiteSpace: 'nowrap',
                                            marginLeft: "8px",
                                            '& .MuiTypography-root': {
                                                fontWeight: 500,
                                                fontSize: '18px',
                                                lineHeight: '100%',
                                                letterSpacing: '-0.4px',
                                            },
                                        }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    )}
                </Grid2>

                <Grid2 size={{ xs: 12, md: 6 }}>
                    {isLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                            <Skeleton variant="circular" width={280} height={280} />
                        </Box>
                    ) : (
                        <HighchartsReact highcharts={Highcharts} options={options} />
                    )}
                </Grid2>
            </Grid2>
        </Box>
    );

};

export default PlanVsActualConstruction;
